export interface PullQuoteProps {
  /**
   * The words. An array is set as separate lines and is how a verse keeps its metre —
   * a string that happened to contain newlines would be collapsed by HTML whitespace
   * handling and the poem would come out as prose.
   */
  text: string | string[]
  /** Who said it. Omit for an unattributed welcome or a house motto. */
  attribution?: string
  /** Their role, under the name, in small caps. */
  role?: string
  /**
   * `verse` sets the whole quote in the handwriting face and narrows the measure. Use it
   * for something written in lines; leave it off for prose someone said.
   */
  variant?: 'quote' | 'verse'
  /** Extra classes on the wrapper. */
  className?: string
}

/**
 * Someone's words, given the width of a page to themselves.
 *
 * The one block on the site with a decorative register, and the reason it exists: a
 * resort is sold on how a place feels, and a quotation is the only element that can say
 * that without describing it. Two variants — prose someone said, and a verse written in
 * lines.
 */
export function PullQuote({ text, attribution, role, variant = 'quote', className }: PullQuoteProps) {
  const lines = Array.isArray(text) ? text : [text]
  const classes = [
    'ul-pullquote',
    variant === 'verse' ? 'ul-pullquote--verse' : '',
    className ?? '',
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <figure className={classes}>
      <span className="ul-pullquote-mark" aria-hidden="true">
        <i className="flaticon-quotation" />
      </span>
      <blockquote className="ul-pullquote-text">
        {/* Keyed by index, not by the line: a verse with a repeated refrain would
            otherwise give two children the same key. */}
        {lines.map((line, i) => (
          <span className="ul-pullquote-line" key={i}>
            {line}
          </span>
        ))}
      </blockquote>
      {attribution && (
        <figcaption>
          <span className="ul-pullquote-attr">{attribution}</span>
          {role && <span className="ul-pullquote-role">{role}</span>}
        </figcaption>
      )}
    </figure>
  )
}
